import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { TipoCanalRepository, TipoCanal, TipoCanalBackendResponse } from 'src/app/domain/service/tipo-canal/tipo-canal-repository';
import { DireccionCursor, PageResponse } from 'src/app/domain/model/common/page-response.dto';
import { validarSize } from 'src/app/common/validation-utils';

/**
 * Adaptador HTTP: HttpTipoCanalRepository
 *
 * Implementa el puerto TipoCanalRepository para comunicación con backend.
 * Realiza llamadas REST al TipoCanalController en servicios-ms.
 *
 * Migrado del legacy GWT UtilidadesBuscadores.buscarTipoCanal().
 * Paginación keyset/cursor del servidor (size, cursor, direction).
 *
 * Arquitectura Hexagonal:
 * - Este adaptador está en infraestructura
 * - Implementa el puerto TipoCanalRepository del dominio
 * - Modelo y puerto ubicados en: domain/service/catalogo/tipocanal/
 */
@Injectable({ providedIn: 'root' })
export class HttpTipoCanalRepository implements TipoCanalRepository {

  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.backend.baseUrl}/servicios/tipos-canal`;

  /**
   * Mapea respuesta del backend al modelo TipoCanal del dominio.
   */
  private mapToTipoCanal(response: TipoCanalBackendResponse): TipoCanal {
    return {
      codigo: response.nmconsecutivo,
      descripcion: response.dstexto
    };
  }

  /**
   * Página de tipos de canal (keyset/cursor, seguridad-girop §2.3).
   * Endpoint: GET /api/servicios/tipos-canal?filtro=F&size=N&direction=D[&cursor=C]
   */
  buscarPagina(
    filtro: string,
    cursor: string | null,
    size: number,
    direction: DireccionCursor
  ): Promise<PageResponse<TipoCanal>> {
    validarSize(size);
    let params = new HttpParams()
      .set('size', String(size))
      .set('direction', direction);
    if (filtro?.trim()) params = params.set('filtro', filtro.trim());
    if (cursor) params = params.set('cursor', cursor);

    return firstValueFrom(
      this.http
        .get<PageResponse<TipoCanalBackendResponse>>(this.baseUrl, { params })
        .pipe(
          map((page) => ({
            ...page,
            content: (page?.content ?? []).map((item) => this.mapToTipoCanal(item))
          }))
        )
    );
  }
}
